import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, PenSquare, ListChecks, UserCheck, MessageSquare, Tags, Users, ScrollText, Settings, LogOut, ExternalLink, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasRole, isDesk } from "@/lib/api";
import { ROLE_LABELS } from "./StatusChip";
import { Brand } from "@/components/news/Brand";

const NAV = [
  { to: "/desk", label: "Dashboard", icon: LayoutDashboard, end: true, roles: null },
  { to: "/desk/stories", label: "Stories", icon: FileText, end: true, roles: null },
  { to: "/desk/stories/new", label: "New story", icon: PenSquare, roles: ["editor", "reporter"] },
  { to: "/desk/queue", label: "Approval queue", icon: ListChecks, roles: ["news_approver"] },
  { to: "/desk/reporters", label: "Reporters", icon: UserCheck, roles: ["reporter_approver"] },
  { to: "/desk/comments", label: "Comments", icon: MessageSquare, roles: ["editor", "news_approver"] },
  { to: "/desk/taxonomy", label: "Categories & tags", icon: Tags, roles: ["editor"] },
  { to: "/desk/users", label: "Users & roles", icon: Users, roles: [] },
  { to: "/desk/audit", label: "Audit log", icon: ScrollText, roles: ["reporter_approver", "news_approver"] },
  { to: "/desk/settings", label: "Site settings", icon: Settings, roles: [] },
];

export const RoleGate = ({ roles, children }) => {
  const { user } = useAuth();
  return hasRole(user, ...roles) ? children : <p data-testid="access-denied" className="p-8 text-zinc-500">You do not have access to this page.</p>;
};

export default function DeskLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  if (user === null) return <div className="p-10 text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (!isDesk(user)) return <Navigate to="/" replace />;

  const items = NAV.filter((n) => !n.roles || hasRole(user, ...n.roles));
  const navCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${isActive ? "bg-[#0B2A5B] text-white" : "text-zinc-700 hover:bg-blue-50"}`;

  const sidebar = (
    <aside className="w-64 shrink-0 bg-white border-r border-zinc-200 h-full flex flex-col">
      <div className="px-5 py-5 border-b border-zinc-200">
        <Brand variant="compact" testId="desk-brand" />
        <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-2">Newsroom desk</p>
      </div>
      <nav data-testid="desk-sidebar" className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={navCls} onClick={() => setOpen(false)}
            data-testid={`desk-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-zinc-200 text-sm">
        <p data-testid="desk-user-name" className="font-semibold truncate">{user.name}</p>
        <p data-testid="desk-user-roles" className="text-xs text-zinc-500">{user.roles.map((r) => ROLE_LABELS[r]).join(", ")}</p>
        <div className="mt-3 flex gap-3">
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid="desk-view-site"><ExternalLink className="h-3.5 w-3.5" /> Site</Link>
          <button onClick={logout} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid="desk-logout-btn"><LogOut className="h-3.5 w-3.5" /> Log out</button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      <div className="hidden lg:block sticky top-0 h-screen">{sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          {sidebar}
          <button className="flex-1 bg-black/40" onClick={() => setOpen(false)} aria-label="Close menu" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between bg-white border-b border-zinc-200 px-4 py-3">
          <span className="font-serif-head font-black">Newsroom desk</span>
          <button data-testid="desk-mobile-menu" onClick={() => setOpen(!open)}>{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]"><Outlet /></main>
      </div>
    </div>
  );
}

export const PageHead = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div>
      <h1 data-testid="desk-page-title" className="font-serif-head font-black text-3xl tracking-tight">{title}</h1>
      {sub && <p className="text-sm text-zinc-500 mt-1">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
